/**
 * TanStack Query hooks for Install Flow API
 */

import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get, PaginatedResponse } from "@/api/api";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type InstallFlowData = {
  id: string;
  /** Null when this is the default fallback flow. */
  equipment_type: string | null;
  label: string;
  is_active: boolean;
  /** True when the backend returned the default fallback flow rather than a specific one. */
  is_default: boolean;
  created_at: string;
  updated_at: string;
};

export type InstallFlowStepData = {
  id: string;
  order: number;
  title: string;
  body: string;
  image: string | null;
  alert_callout: string | null;
  created_at: string;
  updated_at: string;
};

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const installFlows = createQueryKeys("install-flows", {
  list: () => ({
    queryKey: ["list"],
    queryFn: () => get<PaginatedResponse<InstallFlowData>>("install-flows/"),
  }),
  detail: (equipmentType: string) => ({
    queryKey: [equipmentType],
    queryFn: () =>
      get<InstallFlowData>(`install-flows/${equipmentType}/`),
    contextQueries: {
      steps: {
        queryKey: null,
        queryFn: () =>
          get<PaginatedResponse<InstallFlowStepData>>(
            `install-flows/${equipmentType}/steps/`
          ),
      },
    },
  }),
});

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * List all active install flows.
 * Useful for admin/discovery — most UI should use `useInstallFlow` directly.
 */
export const useInstallFlowList = () =>
  useSuspenseQuery(installFlows.list());

/**
 * Fetch the install flow for a specific equipment type.
 *
 * The backend handles the default fallback — if no specific flow exists for
 * the requested equipment type it returns the default flow automatically.
 * Check `data.is_default` to know which was returned.
 *
 * Uses `useQuery` rather than `useSuspenseQuery` because the equipment type
 * may not be known yet (e.g. waiting on the equipment model to load).
 * `enabled: !!equipmentType` suspends the query until the type is available
 * without needing a wrapping Suspense boundary.
 *
 * `retry: false` on 404 prevents 3 redundant retries — a 404 here means no
 * flow and no default are configured, which won't resolve on retry.
 *
 * @param equipmentType - asset_type value from EquipmentModel, e.g. "rooftop_unit"
 */
export const useInstallFlow = (
  equipmentType: string | null | undefined
) =>
  useQuery({
    ...installFlows.detail(equipmentType!),
    enabled: !!equipmentType,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });

/**
 * Fetch the ordered steps for an install flow by equipment type.
 *
 * Same backend fallback and `useQuery` rationale as `useInstallFlow` above.
 *
 * @param equipmentType - asset_type value from EquipmentModel, e.g. "rooftop_unit"
 */
export const useInstallFlowSteps = (
  equipmentType: string | null | undefined
) =>
  useQuery({
    ...installFlows.detail(equipmentType!)._ctx.steps,
    enabled: !!equipmentType,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
